import React, { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { FormInput } from "../../../SharedComponents/FormInput/FormInput";
import { Button, ButtonLoading } from "../../../SharedComponents/Button/Button";

import { InlineInfoBox } from "../../../SharedComponents/InlineInfoBox/InlineInfoBox";
import { useLoginAndRedirect } from "../../../../Hooks/useLoginAndRedirect";

/**
 * @typedef {'IDLE' | 'LOADING' |'LOGIN_ERROR' } ComponentState
 */

export const LoginOnboardingLoginForm = ({ defaultEmail, defaultRedirect }) => {
  /** @type {[ComponentState, (state: ComponentState) => void]} */
  const [componentState, setComponentState] = useState("IDLE");

  const { t } = useTranslation();

  // Setup Login and redirect hook
  const loginAndRedirect = useLoginAndRedirect();

  // Reference to the password input for focusing
  const passwordInputRef = useRef();

  // Focus the password field on mount
  useEffect(
    () => passwordInputRef.current && passwordInputRef.current.focus(),
    []
  );

  // Log the user in and redirect to the correct urls
  async function onLoginSubmit(formData) {
    setComponentState("LOADING");

    try {
      await loginAndRedirect({
        email: formData.email,
        password: formData.password,
        redirectLocation: defaultRedirect,
      });
    } catch (err) {
      return setComponentState("LOGIN_ERROR");
    }
  }

  // Formik form configuration
  const formConfig = {
    initialValues: {
      email: defaultEmail,
      password: "",
    },
    onSubmit: onLoginSubmit,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email("validation.emailInvalid")
        .required("validation.emailRequired"),
      password: Yup.string().required("validation.passwordRequired"),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  };

  const isLoading = componentState === "LOADING";
  const showLoginError = componentState === "LOGIN_ERROR";

  return (
    <Formik {...formConfig}>
      {() => (
        <Form>
          <FormInput
            name="email"
            type="text"
            label="generic.email"
            disabled={true}
          />
          <FormInput
            name="password"
            type="password"
            label="generic.password"
            ref={passwordInputRef}
          />
          {showLoginError && (
            <InlineInfoBox type="danger">
              {t("loginForm.couldNotLogIn")}
            </InlineInfoBox>
          )}
          <div className="LoginOnboardingForm__submit">
            <Link
              to="/login/reset"
              className="LoginOnboardingForm__forgotten-password"
            >
              {t("loginForm.forgotPassword")}
            </Link>
            <div className="LoginOnboardingForm__submit-button">
              <Button
                buttonStyle="Button__primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading && <ButtonLoading />}
                {t("loginForm.loginButton")}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
